import React from 'react';
import { Card, Tag, Descriptions } from 'antd';
import type { Document } from '@/shared/api/types';
import {
  FileTextOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileMarkdownOutlined,
  FileUnknownOutlined
} from '@ant-design/icons';

interface DocumentInfoPanelProps {
  document?: Document;
}

const DocumentInfoPanel: React.FC<DocumentInfoPanelProps> = ({ document }) => {
  if (!document) {
    return (
      <Card title="文档信息" size="small">
        <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
          未选择文档
        </div>
      </Card>
    );
  }

  // 根据文件类型选择图标
  const getFileIcon = () => {
    const fileType = document.file_type?.toLowerCase();
    if (fileType === 'pdf') return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
    if (fileType === 'doc' || fileType === 'docx') return <FileWordOutlined style={{ color: '#1890ff' }} />;
    if (fileType === 'md' || fileType === 'markdown') return <FileMarkdownOutlined style={{ color: '#52c41a' }} />;
    if (fileType === 'txt') return <FileTextOutlined style={{ color: '#666' }} />;
    return <FileUnknownOutlined style={{ color: '#999' }} />;
  };

  // 格式化文件大小
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 格式化日期
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-CN') + ' ' + date.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // 状态标签颜色
  const getStatusColor = (status: string): string => {
    const statusMap: Record<string, string> = {
      pending: 'blue',
      parsing: 'cyan',
      semantic_processing: 'geekblue',
      embedding: 'purple',
      persisting: 'orange',
      completed: 'green',
      failed: 'red',
      active: 'green',
      unknown: 'default'
    };
    return statusMap[status.toLowerCase()] || 'default';
  };

  // 状态文本映射
  const getStatusText = (status: string): string => {
    const statusTextMap: Record<string, string> = {
      pending: '待处理',
      parsing: '解析中',
      semantic_processing: '语义处理',
      embedding: '向量化',
      persisting: '持久化',
      completed: '已完成',
      failed: '失败',
      active: '活跃',
      unknown: '未知'
    };
    return statusTextMap[status] || status;
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {getFileIcon()}
          <span>文档信息</span>
        </div>
      }
      size="small"
    >
      <Descriptions column={1} size="small">
        <Descriptions.Item label="文件名">
          <div style={{ fontWeight: 500 }}>{document.file_name}</div>
        </Descriptions.Item>

        <Descriptions.Item label="文件类型">
          <Tag color="blue">{document.file_type?.toUpperCase() || '未知'}</Tag>
        </Descriptions.Item>

        <Descriptions.Item label="文件大小">
          {formatFileSize(document.file_size || 0)}
        </Descriptions.Item>

        <Descriptions.Item label="处理状态">
          <Tag color={getStatusColor(document.status)}>
            {getStatusText(document.status)}
          </Tag>
        </Descriptions.Item>

        <Descriptions.Item label="创建时间">
          {formatDate(document.created_at)}
        </Descriptions.Item>

        <Descriptions.Item label="Chunks总数">
          <Tag color={document.total_chunks > 0 ? 'green' : 'orange'}>
            {document.total_chunks}
          </Tag>
        </Descriptions.Item>

        {document.document_description && (
          <Descriptions.Item label="文档描述">
            <div style={{
              maxHeight: '60px',
              overflowY: 'auto',
              fontSize: '12px',
              color: '#666'
            }}>
              {document.document_description}
            </div>
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
};

export default DocumentInfoPanel;